import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

/**
 * StatusOrb, non-pulsing dot. Used only for hard error states.
 */
export function StatusOrb({
  tone = 'destructive',
  size = 'sm',
  className,
}: {
  tone?: 'destructive' | 'muted'
  size?: 'xs' | 'sm'
  className?: string
}) {
  const tones: Record<string, string> = {
    destructive: 'bg-destructive',
    muted: 'bg-muted-foreground/40',
  }
  const sizes: Record<string, string> = {
    xs: 'size-1.5',
    sm: 'size-2',
  }
  return (
    <span
      className={['inline-block rounded-full shrink-0', sizes[size], tones[tone], className]
        .filter(Boolean)
        .join(' ')}
    />
  )
}

import {
  Box,
  Braces,
  Compass,
  Container,
  FileCode,
  Gem,
  Layers,
  Package,
  Package2,
} from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Eyebrow, small uppercase technical label with a leading accent bar.
 */
export function Eyebrow({
  children,
  className,
  marker = false,
}: {
  children: ReactNode
  className?: string
  /** When true, prefix with a small primary-colored bar. */
  marker?: boolean
}) {
  return (
    <span
      className={cn(
        'font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground inline-flex items-center gap-2',
        className,
      )}
    >
      {marker && (
        <span
          className="inline-block w-2 h-px bg-primary"
          aria-hidden
        />
      )}
      {children}
    </span>
  )
}

/**
 * Codeline, small inline command/code chip.
 */
export function Codeline({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <code
      className={cn(
        'font-mono text-[11px] px-1.5 py-0.5 rounded bg-secondary text-foreground/80 border border-border',
        className,
      )}
    >
      {children}
    </code>
  )
}

/**
 * BigNumber, large thin-weight numerical display.
 */
export function BigNumber({
  value,
  className,
  unit,
}: {
  value: string
  className?: string
  unit?: string
}) {
  return (
    <span className={cn('flex items-baseline gap-1.5 leading-none', className)}>
      <span className="font-numeral text-foreground tabular-nums">{value}</span>
      {unit && (
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground translate-y-[-0.2em]">
          {unit}
        </span>
      )}
    </span>
  )
}

/**
 * EcosystemMark, distinct colored tile per ecosystem using Lucide icons.
 */
const ECOSYSTEM_TONES: Record<
  string,
  { bg: string; ring: string; text: string; icon: LucideIcon }
> = {
  pypi: { bg: 'bg-[#3776AB]/15', ring: 'ring-[#3776AB]/40', text: 'text-[#5d9fd6]', icon: FileCode },
  npm: { bg: 'bg-[#CB3837]/15', ring: 'ring-[#CB3837]/40', text: 'text-[#e67978]', icon: Package },
  cargo: { bg: 'bg-[#dea584]/15', ring: 'ring-[#dea584]/40', text: 'text-[#dea584]', icon: Container },
  maven: { bg: 'bg-[#c71a36]/15', ring: 'ring-[#c71a36]/40', text: 'text-[#e75a72]', icon: Layers },
  rubygems: { bg: 'bg-[#A91401]/15', ring: 'ring-[#A91401]/40', text: 'text-[#e35a4a]', icon: Gem },
  nuget: { bg: 'bg-[#004880]/15', ring: 'ring-[#004880]/40', text: 'text-[#5b97cf]', icon: Package2 },
  go: { bg: 'bg-[#00ADD8]/15', ring: 'ring-[#00ADD8]/40', text: 'text-[#5bccea]', icon: Compass },
  docker: { bg: 'bg-[#1D63ED]/15', ring: 'ring-[#1D63ED]/40', text: 'text-[#5e88e8]', icon: Container },
  composer: { bg: 'bg-[#885630]/15', ring: 'ring-[#885630]/40', text: 'text-[#d49a6a]', icon: Braces },
  generic: { bg: 'bg-primary/10', ring: 'ring-primary/30', text: 'text-primary', icon: Box },
}

function ecosystemTone(eco: string) {
  const generic = ECOSYSTEM_TONES.generic!
  return ECOSYSTEM_TONES[eco.toLowerCase()] ?? generic
}

export function EcosystemMark({
  ecosystem,
  size = 'md',
  className,
}: {
  ecosystem: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  const tone = ecosystemTone(ecosystem)
  const Icon = tone.icon
  const SIZE: Record<'sm' | 'md' | 'lg', { box: string; icon: string }> = {
    sm: { box: 'size-6 rounded-[5px]', icon: 'size-3' },
    md: { box: 'size-9 rounded-md', icon: 'size-4' },
    lg: { box: 'size-12 rounded-lg', icon: 'size-5' },
  }
  const s = SIZE[size]
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center ring-1 ring-inset shrink-0',
        s.box,
        tone.bg,
        tone.ring,
        tone.text,
        className,
      )}
      aria-label={ecosystem}
    >
      <Icon className={s.icon} strokeWidth={1.75} />
    </span>
  )
}

export function EcosystemBadge({
  ecosystem,
  className,
}: {
  ecosystem: string
  className?: string
}) {
  const tone = ecosystemTone(ecosystem)
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 font-mono uppercase text-[10px] tracking-[0.14em] px-1.5 py-0.5 rounded ring-1 ring-inset',
        tone.bg,
        tone.ring,
        tone.text,
        className,
      )}
    >
      <span className="size-1 rounded-full bg-current" />
      {ecosystem}
    </span>
  )
}

/**
 * Hairline, decorative divider with optional label.
 */
export function Hairline({
  label,
  align = 'left',
  className,
}: {
  label?: ReactNode
  align?: 'left' | 'center' | 'right'
  className?: string
}) {
  if (!label) {
    return <div className={cn('h-px w-full bg-border', className)} role="separator" />
  }
  const justify =
    align === 'center'
      ? 'justify-center'
      : align === 'right'
        ? 'justify-end'
        : 'justify-start'
  return (
    <div className={cn('flex items-center gap-3 w-full', justify, className)} role="separator">
      <span className="h-px flex-1 bg-border" />
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        {label}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  )
}

/**
 * Frame, primary visual container.
 */
export function Frame({
  children,
  className,
  grid = false,
  glow = false,
}: {
  children: ReactNode
  className?: string
  grid?: boolean
  glow?: boolean
}) {
  return (
    <div className={cn('relative border border-border bg-card rounded-md', className)}>
      {grid && (
        <div className="absolute inset-0 hawk-grid-fine pointer-events-none opacity-60" aria-hidden />
      )}
      {glow && (
        <div
          className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent pointer-events-none"
          aria-hidden
        />
      )}
      <div className="relative">{children}</div>
    </div>
  )
}

/**
 * MonoTag, small monospace label.
 */
export function MonoTag({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground',
        className,
      )}
    >
      {children}
    </span>
  )
}

